import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import {
    Campaign,
    Requirements,
    ScreeningQuestion,
    ScreeningQuestionType,
} from 'types/campaign'
import { Contact } from 'types/creators'
import { SocialAccount } from 'types/influencer'
import { RootState } from 'store'
import inModal from 'utils/hoc/inModal'
import ScreeningQuestionBinary from './ScreeningQuestion/ScreeningQuestionBinary'
import ScreeningQuestionMCQ from './ScreeningQuestion/ScreeningQuestionMCQ'
import ScreeningQuestionText from './ScreeningQuestion/ScreeningQuestionText'
import './CampaignApplicationConfirmationModal.scss'

export type ApplicationSubmitError = {
    error_code?: string
    question_id?: number
    missing_permissions?: Record<string, string>
}

interface CampaignApplicationConfirmationModalProps {
    isActive: boolean
    onClose: () => void
    applicant?: { creator: Contact; socialAccount: SocialAccount }
    campaign: Campaign
    onApply: (languageId?: string, answers?: Record<string, string | number>) => void
    applicationError?: ApplicationSubmitError | null;
    questions: ScreeningQuestion[];
}

const CampaignApplicationConfirmationModal: React.FC<
    CampaignApplicationConfirmationModalProps
> = ({
    applicant,
    campaign,
    onApply,
    applicationError,
    questions,
}) => {
    const languages = useSelector((state: RootState) => state.auth.master?.language_list) ?? {}
    const [selectedLanguage, setSelectedLanguage] = useState<{ label: string; value: string } | null>(
        null
    )
    const [answers, setAnswers] = useState<Record<string, string | number> | undefined>(undefined)

    const campaignRequirements = campaign.requirements

    const [isApplying, setIsApplying] = useState(false);

    useEffect(() => {
        if (questions.length > 0) {
            const initial: Record<string, string> = {}
            questions.forEach((q) => {
                initial[q.id.toString()] = ''
            })
            setAnswers(initial)
        } else {
            setAnswers(undefined)
        }
    }, [campaign.uuid, questions])

    useEffect(() => {
        setSelectedLanguage(null)
    }, [campaign.uuid])

    const eligibleLanguages = useMemo(() => {
        const options: { label: string; value: string }[] = []
        if (campaignRequirements?.languages?.length) {
            campaignRequirements.languages.forEach((languageId) => {
                if (languages[languageId]) {
                    options.push({ label: languages[languageId], value: languageId })
                }
            })
        }
        return options
    }, [campaignRequirements, languages])

    const allQuestionsAnswered = useMemo(() => {
        if (!questions.length) return true
        return questions.every((q) => {
            const answer = answers?.[q.id.toString()]
            if (answer === undefined || answer === '') return false
            if (q.type === ScreeningQuestionType.BINARY) {
                return answer === 0 || answer === 1
            }
            return true
        })
    }, [questions, answers])

    const updateAnswer = (questionId: number, value: string | number) => {
        setAnswers((prev) => ({
            ...(prev ?? {}),
            [questionId.toString()]: value,
        }))
    }

    const renderQuestion = (question: ScreeningQuestion, index: number) => {
        const highlightError =
            applicationError?.error_code === 'invalid_answer' &&
            applicationError.question_id === question.id
        const value = answers?.[question.id.toString()] ?? ''
        const common = {
            key: question.id,
            index,
            question,
            value,
            highlightError,
            onChange: (v: string | number) => updateAnswer(question.id, v),
        }

        if (question.type === ScreeningQuestionType.CHOICE) {
            return <ScreeningQuestionMCQ {...common} />
        }
        if (question.type === ScreeningQuestionType.BINARY) {
            return <ScreeningQuestionBinary {...common} />
        }
        if (question.type === ScreeningQuestionType.TEXT) {
            return <ScreeningQuestionText {...common} />
        }
        return null
    }

    const applyCampaign = () => {
        if (!allQuestionsAnswered) {
            toast.error('Please answer all questions')
            return
        }
        if (eligibleLanguages.length > 0 && !selectedLanguage) {
            toast.error('Please select a language')
            return
        }
        onApply(selectedLanguage?.value, answers)
    }

    const apply = async () => {
        setIsApplying(true);
        await applyCampaign();
        setIsApplying(false);
    }

    if (!applicant) return null

    const { socialAccount } = applicant
    const profileImage = (socialAccount.profile_picture_url ?? '').split('=')[0]

    return (
        <div className="campaign-apply-confirmation">
            {questions.length > 0 && (
                <div className="campaign-apply-confirmation__questions">
                    {questions.map((question, index) => renderQuestion(question, index))}
                </div>
            )}

            <div className="campaign-apply-confirmation__footer">
                <div className="campaign-apply-confirmation__account">
                    <img
                        src={profileImage}
                        alt=""
                        className="campaign-apply-confirmation__avatar"
                        width={50}
                        height={50}
                    />
                    <div>
                        <span className="campaign-apply-confirmation__account-label">
                            Applying using
                        </span>
                        <span className="campaign-apply-confirmation__account-handle">
                            {socialAccount.handle}
                        </span>
                    </div>
                </div>

                {eligibleLanguages.length > 0 && (
                    <div className="campaign-apply-confirmation__language">
                        <label className="campaign-apply-confirmation__language-label">
                            Please select language which you will use
                        </label>
                        <Select
                            isClearable
                            maxMenuHeight={100}
                            classNamePrefix="campaign-apply-language"
                            className="campaign-apply-confirmation__language-select"
                            placeholder="Select Language*"
                            menuPlacement="top"
                            options={eligibleLanguages}
                            value={selectedLanguage}
                            onChange={(selected: { label: string; value: string } | null) =>
                                setSelectedLanguage(selected)
                            }
                        />
                    </div>
                )}

                <div className="campaign-apply-confirmation__disclaimer">
                    <span className="fw-bold">Disclaimer: </span>
                    <span>Application selection is decided by brand.</span>
                </div>

                <button
                    type="button"
                    className="theme-btn btn-primary campaign-apply-confirmation__submit w-100"
                    disabled={
                        isApplying ||
                        (eligibleLanguages.length > 0 && !selectedLanguage) ||
                        !allQuestionsAnswered
                    }
                    onClick={apply}
                >
                    {isApplying ? 'Applying...' : 'Apply'}
                </button>
            </div>
        </div>
    )
}

export default inModal(CampaignApplicationConfirmationModal, 'Complete Application', {
    id: 'campaign-apply-modal',
    dialogClassName: 'campaign-apply-modal-dialog',
    modalClassName: 'campaign-apply-modal',
})
